import { isIncome, isPayment, participantName } from "./expense";
import { toCents } from "./money";

export const normalizeSearchText = (value) =>
  `${value ?? ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/\s+/g, " ")
    .trim();

const searchTokens = (query) =>
  normalizeSearchText(query)
    .split(" ")
    .filter((token) => token && token !== "€");

const GROUPED_AMOUNT = /^\d{1,3}(\.\d{3})+(,\d{0,2})?$/;
const DECIMAL_AMOUNT = /^\d+([.,]\d{0,2})?$/;

export const parseSearchAmount = (token) => {
  const value = `${token ?? ""}`.replace(/€|eur$/g, "").trim();
  let euros;
  let cents;
  if (GROUPED_AMOUNT.test(value)) {
    [euros, cents] = value.replace(/\./g, "").split(",");
  } else if (DECIMAL_AMOUNT.test(value)) {
    [euros, cents] = value.split(/[.,]/);
  } else {
    return null;
  }
  const whole = parseInt(euros, 10) * 100;
  if (!cents) return { wholeEuros: true, cents: whole };
  return {
    wholeEuros: false,
    cents: whole + parseInt(cents.padEnd(2, "0"), 10),
  };
};

const matchesAmount = (amount, token) => {
  const parsed = parseSearchAmount(token);
  if (!parsed) return false;
  const cents = toCents(amount);
  return parsed.wholeEuros
    ? Math.floor(cents / 100) * 100 === parsed.cents
    : cents === parsed.cents;
};

const receiverIdsOf = (expense) => {
  const receivers = Array.isArray(expense.receivers) ? expense.receivers : [];
  const withShare = receivers.filter(
    (r) => toCents(r?.absolute_share_in_decimals) > 0,
  );
  return (withShare.length ? withShare : receivers).map(
    (r) => r?.participant_id,
  );
};

const tagNames = (expense) =>
  (Array.isArray(expense.tags) ? expense.tags : []).map((tag) =>
    typeof tag === "string" ? tag : tag?.name,
  );

const searchableTexts = (expense, participants) =>
  [
    expense.name,
    typeof expense.description === "string" ? expense.description : "",
    isPayment(expense) ? "Zahlung" : "",
    isIncome(expense) ? "Einnahme" : "",
    participantName(participants, expense.payer, ""),
    ...receiverIdsOf(expense).map((id) =>
      participantName(participants, id, ""),
    ),
    ...tagNames(expense),
  ]
    .filter((text) => typeof text === "string" && text)
    .map(normalizeSearchText);

export const matchesSearch = (expense, query, participants = []) => {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return true;
  if (!expense) return false;
  const texts = searchableTexts(expense, participants);
  return tokens.every(
    (token) =>
      texts.some((text) => text.includes(token)) ||
      matchesAmount(expense.amount_in_decimals, token),
  );
};

export const searchExpenses = (expenses = [], query = "", participants = []) =>
  searchTokens(query).length === 0
    ? expenses
    : expenses.filter((expense) => matchesSearch(expense, query, participants));

export const isSearchActive = (query) => searchTokens(query).length > 0;
